import { Check, X } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Password policy enforced by both the Cognito user pool and the
 * backend signup/reset routes. Kept in one place so the UI rule list,
 * client-side pre-check, and backend regex are actually the same thing.
 *
 * The symbol set is the full Cognito default set
 * (https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html).
 */
export const PASSWORD_ALLOWED_SYMBOLS = '^$*.[]{}()?"!@#%&/\\,><\':;|_~`+=- '

const SYMBOL_RE = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`+=\-\s]/

export type PasswordRuleKey =
  | 'length'
  | 'lowercase'
  | 'uppercase'
  | 'digit'
  | 'symbol'

export type PasswordRuleResult = {
  key: PasswordRuleKey
  label: string
  met: boolean
}

const RULES: { key: PasswordRuleKey; label: string; test: (v: string) => boolean }[] = [
  { key: 'length',    label: 'At least 8 characters',            test: (v) => v.length >= 8 },
  { key: 'lowercase', label: 'One lowercase letter (a–z)',       test: (v) => /[a-z]/.test(v) },
  { key: 'uppercase', label: 'One uppercase letter (A–Z)',       test: (v) => /[A-Z]/.test(v) },
  { key: 'digit',     label: 'One number (0–9)',                 test: (v) => /\d/.test(v) },
  { key: 'symbol',    label: 'One symbol (e.g. ! @ # $ % & * ?)', test: (v) => SYMBOL_RE.test(v) },
]

export function evaluatePassword(password: string): PasswordRuleResult[] {
  return RULES.map((r) => ({ key: r.key, label: r.label, met: r.test(password) }))
}

export function isPasswordStrong(password: string): boolean {
  return evaluatePassword(password).every((r) => r.met)
}

type Props = {
  password: string
  className?: string
}

export function PasswordStrength({ password, className }: Props) {
  const results = evaluatePassword(password)
  const met = results.filter((r) => r.met).length
  const total = results.length
  const pct = Math.round((met / total) * 100)

  const barColor =
    met === 0 ? 'bg-muted'
      : met < 3 ? 'bg-destructive'
      : met < total ? 'bg-amber-500'
      : 'bg-emerald-500'

  const label =
    met === 0 ? 'Enter a password'
      : met < 3 ? 'Weak'
      : met < total ? 'Almost there'
      : 'Strong'

  return (
    <div className={cn('space-y-2 rounded-md border border-border bg-muted/30 p-3', className)}>
      <div className="flex items-center justify-between gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className={cn('h-full transition-all', barColor)}
            style={{ width: `${pct}%` }}
            aria-hidden
          />
        </div>
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <ul className="space-y-1">
        {results.map((r) => (
          <li
            key={r.key}
            className={cn(
              'flex items-center gap-2 text-xs',
              r.met ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground',
            )}
          >
            {r.met ? (
              <Check className="size-3.5 shrink-0" aria-hidden />
            ) : (
              <X className="size-3.5 shrink-0 text-muted-foreground/60" aria-hidden />
            )}
            <span>{r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
